import { useNavigate } from "@tanstack/react-router";
import {
	createColumnHelper,
	flexRender,
	getCoreRowModel,
	getFilteredRowModel,
	getPaginationRowModel,
	getSortedRowModel,
	useReactTable,
} from "@tanstack/react-table";
import { format } from "date-fns";
import {
	CalendarIcon,
	FileDownIcon,
	LoaderIcon,
	SearchIcon,
} from "lucide-react";
import Papa from "papaparse";
import { useMemo, useState } from "react";
import type { DateRange } from "react-day-picker";
import {
	CDR_EXPORT_PAGE_SIZE,
	filterByDateRange,
	getExportDateParams,
} from "@/components/recent-calls-export";
import { Button } from "@/components/ui/button";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from "@/components/ui/popover";
import { Progress } from "@/components/ui/progress";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import { useApiClient } from "@/hooks/api/use-api-client";
import { useOrganizationById } from "@/hooks/queries/use-organization";
import {
	type TenantCdrsResponse,
	useTenantCdrsQuery,
} from "@/hooks/queries/use-tenant-cdrs-query";
import { cn } from "@/lib/utils";

type CdrRecord = TenantCdrsResponse["data"][number];

const DEFAULT_EXPORT_LOOKBACK_DAYS = 7;
const DEFAULT_FULL_VIEW_PAGE_SIZE = 50;
const DEFAULT_DASHBOARD_PAGE_SIZE = 5;
const CDR_QUERY_STALE_TIME_MS = 30_000;
const EXPORT_PROGRESS_PREPARE_PERCENT = 25;
const EXPORT_PROGRESS_FORMAT_PERCENT = 50;
const EXPORT_PROGRESS_SERIALIZE_PERCENT = 75;
const EXPORT_PROGRESS_COMPLETE_PERCENT = 100;
const EXPORT_DIALOG_CLOSE_DELAY_MS = 500;
const CDR_EXPORT_HEADERS = [
	"Call ID",
	"From Number",
	"To Number",
	"Start Time",
	"Answer Time",
	"End Time",
	"Duration (seconds)",
	"Connected (seconds)",
	"Status",
] as const;
type CdrExportHeader = (typeof CDR_EXPORT_HEADERS)[number];
type CdrExportRow = Record<CdrExportHeader, number | string>;

function getDefaultDateRange(): DateRange {
	const now = new Date();
	const from = new Date(now);
	from.setDate(now.getDate() - DEFAULT_EXPORT_LOOKBACK_DAYS);
	return { from, to: now };
}

function filterCdrs(
	records: TenantCdrsResponse["data"],
	term: string,
): TenantCdrsResponse["data"] {
	const query = term.trim().toLowerCase();
	if (!query) {
		return records;
	}

	return records.filter((record) => {
		const haystacks = [
			record.callerIdNumber,
			record.destinationNumber,
			record.callerIdName,
			record.hangupCause,
			record.id,
		];

		return haystacks.some((value) => value?.toLowerCase().includes(query));
	});
}

function createColumns(
	columnHelper: ReturnType<typeof createColumnHelper<CdrRecord>>,
) {
	return [
		columnHelper.accessor("callerIdNumber", {
			header: "From",
			cell: (info) => info.getValue() || "Unknown",
		}),
		columnHelper.accessor("destinationNumber", {
			header: "To",
			cell: (info) => info.getValue() || "-",
		}),
		columnHelper.accessor("duration", {
			header: "Duration",
			cell: (info) => (
				<div>
					<div>{info.getValue() ? `${info.getValue()}s` : "-"}</div>
					{info.row.original.billsec &&
						info.row.original.billsec !== info.getValue() && (
							<div className="text-muted-foreground text-xs">
								Connected: {info.row.original.billsec}s
							</div>
						)}
				</div>
			),
		}),
		columnHelper.accessor("hangupCause", {
			header: "Status",
			cell: (info) => (
				<span
					className={cn(
						"inline-flex items-center rounded-full px-2 py-1 font-medium text-xs",
						{
							"bg-green-100 text-green-700":
								info.getValue() === "NORMAL_CLEARING",
							"bg-yellow-100 text-yellow-700": info.getValue() === "NO_ANSWER",
							"bg-orange-100 text-orange-700": info.getValue() === "USER_BUSY",
							"bg-red-100 text-red-700": ![
								"NORMAL_CLEARING",
								"NO_ANSWER",
								"USER_BUSY",
							].includes(info.getValue() || ""),
						},
					)}
				>
					{info.getValue() || "Unknown"}
				</span>
			),
		}),
		columnHelper.accessor("startStamp", {
			header: "Start Time",
			cell: (info) => {
				const timestamp = info.getValue();
				return timestamp
					? format(new Date(timestamp), "yyyy-MM-dd HH:mm:ss")
					: "-";
			},
		}),
	];
}

function getPageSize(fullView: boolean) {
	return fullView ? DEFAULT_FULL_VIEW_PAGE_SIZE : DEFAULT_DASHBOARD_PAGE_SIZE;
}

function triggerCsvDownloadFromParts(csvParts: string[], filename: string) {
	const blob = new Blob(csvParts, { type: "text/csv;charset=utf-8;" });
	const url = URL.createObjectURL(blob);
	const link = document.createElement("a");
	link.setAttribute("href", url);
	link.setAttribute("download", filename);
	link.style.visibility = "hidden";
	document.body.appendChild(link);
	link.click();
	document.body.removeChild(link);
	URL.revokeObjectURL(url);
}

// Updated props: removed recordingEnabled as it's not relevant for this placeholder
type RecentActivityProps = {
	fullView?: boolean;
	skipCdrCheck?: boolean; // Skip CDR check if already verified by parent component
	searchParams?: { page?: number; search?: string }; // Optional search params from parent
	tenantId: string; // Required tenant ID
};

type RecentCallsSearchState = {
	page?: number;
	search?: string;
};

// We operate directly on the API response shape returned by the CDR endpoint

// Renamed component to reflect placeholder nature
export function RecentCalls({
	fullView = false,
	skipCdrCheck = false,
	searchParams: propSearchParams,
	tenantId,
}: RecentActivityProps) {
	"use no memo";

	const navigate = useNavigate();
	const apiClient = useApiClient();

	// Use provided search params or defaults
	const searchParams = propSearchParams || { page: 0, search: undefined };

	const { organization } = useOrganizationById(tenantId);
	const cdrEnabled = skipCdrCheck ? true : (organization?.enableCdr ?? false);

	const [searchTerm, setSearchTerm] = useState(searchParams.search || "");
	const [isExportDialogOpen, setIsExportDialogOpen] = useState(false);
	const [isExporting, setIsExporting] = useState(false);
	const [exportProgress, setExportProgress] = useState(0);
	const [exportDateRange, setExportDateRange] = useState<DateRange | undefined>(
		() => getDefaultDateRange(),
	);

	const { data: cdrsResponse, isLoading } = useTenantCdrsQuery(
		{
			tenantId,
			page: 0,
			pageSize: CDR_EXPORT_PAGE_SIZE,
		},
		{
			enabled: !!tenantId && cdrEnabled,
			staleTime: CDR_QUERY_STALE_TIME_MS,
		},
	);

	const allCdrs = cdrsResponse?.data ?? [];

	const columnHelper = createColumnHelper<TenantCdrsResponse["data"][number]>();

	// Filter CDRs based on search term - done on all data for proper pagination
	const filteredCdrs = useMemo(
		() => filterCdrs(allCdrs, searchTerm),
		[allCdrs, searchTerm],
	);

	const exportableCdrs = useMemo(
		() => filterByDateRange(allCdrs, exportDateRange),
		[allCdrs, exportDateRange],
	);
	const exportableCount = exportableCdrs.length;

	// TanStack Table Columns Definition
	const columns = useMemo(() => createColumns(columnHelper), [columnHelper]);

	// TanStack Table Instance
	// eslint-disable-next-line react-hooks-js/incompatible-library
	const table = useReactTable({
		data: filteredCdrs,
		columns,
		getCoreRowModel: getCoreRowModel(),
		getPaginationRowModel: getPaginationRowModel(),
		getFilteredRowModel: getFilteredRowModel(),
		getSortedRowModel: getSortedRowModel(),
		initialState: {
			pagination: {
				pageSize: getPageSize(fullView),
			},
		},
	});

	// Format CDR data for CSV export
	const formatCdrForExport = (
		cdr: TenantCdrsResponse["data"][number],
	): CdrExportRow => ({
		"Call ID": cdr.id,
		"From Number": cdr.callerIdNumber || "Unknown",
		"To Number": cdr.destinationNumber || "",
		"Start Time": cdr.startStamp
			? format(new Date(cdr.startStamp), "yyyy-MM-dd HH:mm:ss")
			: "",
		"Answer Time": cdr.answerStamp
			? format(new Date(cdr.answerStamp), "yyyy-MM-dd HH:mm:ss")
			: "",
		"End Time": cdr.endStamp
			? format(new Date(cdr.endStamp), "yyyy-MM-dd HH:mm:ss")
			: "",
		"Duration (seconds)": cdr.duration || 0,
		"Connected (seconds)": cdr.billsec || 0,
		Status: cdr.hangupCause || "Unknown",
	});

	const buildCsvPartsForExport = async () => {
		const csvParts: string[] = [];
		const { startDate, endDate } = getExportDateParams(exportDateRange);
		let offset = 0;
		let total = 0;
		let firstChunk = true;

		while (true) {
			const response = await apiClient<TenantCdrsResponse>(
				`/api/organizations/${tenantId}/cdrs`,
				{
					params: {
						limit: CDR_EXPORT_PAGE_SIZE,
						offset,
						startDate,
						endDate,
					},
				},
			);

			total = response.total;

			const csvData = response.data.map(formatCdrForExport);
			if (csvData.length > 0) {
				const csvChunk = Papa.unparse(csvData, {
					header: firstChunk,
					skipEmptyLines: true,
				});

				if (csvChunk.length > 0) {
					if (!firstChunk) {
						csvParts.push("\n");
					}
					csvParts.push(csvChunk);
					firstChunk = false;
				}
			}

			if (total > 0) {
				const progressSpan =
					EXPORT_PROGRESS_FORMAT_PERCENT - EXPORT_PROGRESS_PREPARE_PERCENT;
				const fetchedRecords = Math.min(total, offset + response.data.length);
				const progress = Math.round((fetchedRecords / total) * progressSpan);
				setExportProgress(
					Math.min(
						EXPORT_PROGRESS_FORMAT_PERCENT,
						EXPORT_PROGRESS_PREPARE_PERCENT + progress,
					),
				);
			}

			if (
				response.data.length === 0 ||
				offset + response.data.length >= total
			) {
				break;
			}

			offset += response.data.length;
			await new Promise<void>((resolve) => {
				window.setTimeout(resolve, 0);
			});
		}

		if (csvParts.length === 0) {
			csvParts.push(`${CDR_EXPORT_HEADERS.join(",")}\n`);
		}

		return csvParts;
	};

	const handleExportCdr = async () => {
		setIsExporting(true);
		setExportProgress(0);

		try {
			setExportProgress(EXPORT_PROGRESS_PREPARE_PERCENT);
			const csvParts = await buildCsvPartsForExport();
			setExportProgress(EXPORT_PROGRESS_FORMAT_PERCENT);

			setExportProgress(EXPORT_PROGRESS_SERIALIZE_PERCENT);

			const fromDate = exportDateRange?.from
				? format(exportDateRange.from, "yyyy-MM-dd")
				: "all";
			const toDate = exportDateRange?.to
				? format(exportDateRange.to, "yyyy-MM-dd")
				: "all";
			const filename = `call-logs-${fromDate}-to-${toDate}.csv`;

			triggerCsvDownloadFromParts(csvParts, filename);
			setExportProgress(EXPORT_PROGRESS_COMPLETE_PERCENT);

			window.setTimeout(() => {
				setIsExportDialogOpen(false);
				setIsExporting(false);
				setExportProgress(0);
			}, EXPORT_DIALOG_CLOSE_DELAY_MS);
		} catch (error) {
			console.error("Failed to export CDR data", error);
			setIsExporting(false);
			setExportProgress(0);
		}
	};

	const handleSearchChange = (value: string) => {
		setSearchTerm(value);
		table.setPageIndex(0);
		if (fullView) {
			navigate({
				to: ".",
				search: (prev: RecentCallsSearchState) => ({
					...prev,
					search: value,
					page: 0,
				}),
			});
		}
	};

	const renderToolbar = () => {
		if (!fullView) {
			return null;
		}

		return (
			<div className="mb-4 flex items-center justify-between">
				<div className="relative">
					<SearchIcon className="absolute top-1/2 left-2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
					<Input
						aria-label="Search call logs"
						className="h-8 w-[150px] pl-8 lg:w-[250px]"
						disabled={!cdrEnabled}
						onChange={(event) => handleSearchChange(event.target.value)}
						placeholder="Search call logs..."
						value={searchTerm}
					/>
				</div>
				<Dialog onOpenChange={setIsExportDialogOpen} open={isExportDialogOpen}>
					<Button
						disabled={!cdrEnabled}
						onClick={() => setIsExportDialogOpen(true)}
						variant="outline"
					>
						<FileDownIcon className="mr-2 h-4 w-4" />
						{cdrEnabled ? "Export CDR" : "Export CDR (Disabled)"}
					</Button>
					<DialogContent className="sm:max-w-[500px]">
						<DialogHeader>
							<DialogTitle>Export Call Detail Records</DialogTitle>
							<DialogDescription>
								Select a date range to export your call logs as a CSV file.
							</DialogDescription>
						</DialogHeader>
						<div className="grid gap-4 py-4">
							<div className="grid grid-cols-4 items-center gap-4">
								<Label className="text-right" htmlFor="date-range">
									Date Range
								</Label>
								<div className="col-span-3">
									<Popover>
										<PopoverTrigger asChild>
											<Button
												className={cn(
													"w-full justify-start text-left font-normal",
													!exportDateRange && "text-muted-foreground",
												)}
												disabled={isExporting}
												id="date-range"
												variant="outline"
											>
												<CalendarIcon className="mr-2 h-4 w-4" />
												{exportDateRange?.from ? (
													exportDateRange.to ? (
														<>
															{format(exportDateRange.from, "LLL dd, y")} -{" "}
															{format(exportDateRange.to, "LLL dd, y")}
														</>
													) : (
														format(exportDateRange.from, "LLL dd, y")
													)
												) : (
													<span>Pick a date range</span>
												)}
											</Button>
										</PopoverTrigger>
										<PopoverContent align="start" className="w-auto p-0">
											<CalendarComponent
												defaultMonth={exportDateRange?.from}
												disabled={(date) =>
													date > new Date() || date < new Date("2020-01-01")
												}
												mode="range"
												numberOfMonths={2}
												onSelect={setExportDateRange}
												selected={exportDateRange}
											/>
										</PopoverContent>
									</Popover>
								</div>
							</div>
							{isExporting && (
								<div className="space-y-2">
									<div className="flex items-center justify-between text-sm">
										<span className="text-muted-foreground">
											Exporting call logs...
										</span>
										<span className="font-medium">{exportProgress}%</span>
									</div>
									<Progress className="h-2" value={exportProgress} />
								</div>
							)}
							<div className="text-muted-foreground text-sm">
								{exportableCount} calls will be exported
							</div>
						</div>
						<DialogFooter>
							<Button
								disabled={isExporting}
								onClick={() => setIsExportDialogOpen(false)}
								variant="outline"
							>
								Cancel
							</Button>
							<Button disabled={isExporting} onClick={handleExportCdr}>
								{isExporting ? (
									<>
										<LoaderIcon className="mr-2 h-4 w-4 animate-spin" />
										Exporting...
									</>
								) : (
									<>
										<FileDownIcon className="mr-2 h-4 w-4" />
										Export CSV
									</>
								)}
							</Button>
						</DialogFooter>
					</DialogContent>
				</Dialog>
			</div>
		);
	};

	const renderTableBody = () => {
		if (isLoading) {
			return (
				<TableRow>
					<TableCell className="h-24 text-center" colSpan={columns.length}>
						<div className="flex items-center justify-center gap-2 text-muted-foreground text-sm">
							<LoaderIcon className="h-4 w-4 animate-spin" />
							Loading call logs...
						</div>
					</TableCell>
				</TableRow>
			);
		}

		const rows = table.getRowModel().rows;

		if (!rows?.length) {
			return (
				<TableRow>
					<TableCell className="h-24 text-center" colSpan={columns.length}>
						<div className="text-muted-foreground text-sm">
							No call logs found for the selected filters.
						</div>
					</TableCell>
				</TableRow>
			);
		}

		return rows.map((row) => (
			<TableRow
				data-state={row.getIsSelected() ? "selected" : undefined}
				key={row.id}
			>
				{row.getVisibleCells().map((cell) => (
					<TableCell key={cell.id}>
						{flexRender(cell.column.columnDef.cell, cell.getContext())}
					</TableCell>
				))}
			</TableRow>
		));
	};

	const renderPagination = () => {
		if (!fullView) {
			return null;
		}

		return (
			<div className="flex items-center justify-end space-x-2 py-4">
				<span className="text-muted-foreground text-sm">
					Page {table.getState().pagination.pageIndex + 1} of{" "}
					{table.getPageCount()}
				</span>
				<Button
					disabled={!table.getCanPreviousPage()}
					onClick={() => table.previousPage()}
					size="sm"
					variant="outline"
				>
					Previous
				</Button>
				<Button
					disabled={!table.getCanNextPage()}
					onClick={() => table.nextPage()}
					size="sm"
					variant="outline"
				>
					Next
				</Button>
			</div>
		);
	};

	// If CDR is disabled, show appropriate message
	if (!cdrEnabled) {
		return (
			<div className="flex h-[400px] items-center justify-center text-center text-muted-foreground">
				<div>
					<p className="mb-2">Call Detail Records are disabled.</p>
					<p className="text-sm">Enable CDR in Settings to view call logs.</p>
				</div>
			</div>
		);
	}

	return (
		<div className="space-y-4">
			{renderToolbar()}
			<Table>
				<TableHeader>
					{table.getHeaderGroups().map((headerGroup) => (
						<TableRow key={headerGroup.id}>
							{headerGroup.headers.map((header) => (
								<TableHead key={header.id}>
									{header.isPlaceholder
										? null
										: flexRender(
												header.column.columnDef.header,
												header.getContext(),
											)}
								</TableHead>
							))}
						</TableRow>
					))}
				</TableHeader>
				<TableBody>{renderTableBody()}</TableBody>
			</Table>
			{renderPagination()}
		</div>
	);
}
